import { ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAcademySelection from '@/composables/useAcademySelection'

export default function useFitnessAssessments(resource = '/assessment/fitness') {
  const RESOURCE = resource

  const { showSnackbar } = useSnackbar()
  const { getContentScopeParams } = useAcademySelection()

  const dtHeaders = [
    {
      text: 'UNIQUE KEY', value: 'uniqueKey',
    },
    {
      text: 'TITLE OF SESSION', value: 'titleOfSession',
    },
    {
      text: 'SESSION TYPE', value: 'sessionType',
    },
    {
      text: 'CATEGORY', value: 'category',
    },
    {
      text: 'LEVEL', value: 'level',
    },
    {
      text: 'FREQUENCY', value: 'frequency',
    },
    {
      text: 'MEASUREMENT', value: 'measurement',
    },
    {
      text: 'PRACTICE TYPE', value: 'practiceType',
    },
    {
      text: 'GOAL', value: 'goal',
    },
    {
      text: 'EXERCISE', value: 'exercise',
    },
    {
      text: 'EX. PRIORITY', value: 'exercisePriority',
    },
    {
      text: 'SCORING DESC.', value: 'scoringDescriptions',
    },
    {
      text: 'TIPS', value: 'tips', sortable: false,
    },
    {
      text: 'ACTIVE', value: 'active', width: 100, class: 'compact-table-column', cellClass: 'compact-table-column',
    },
    {
      text: 'ACTIONS', value: 'actions', sortable: false, width: 100, align: 'center', class: 'compact-table-column', cellClass: 'compact-table-column',
    },
  ]
  const dtSearchQuery = ref('')
  const dtExpanded = ref([])

  const blankFitnessAssessment = {
    id: '',
    uniqueKey: '',
    sessionType: '',
    titleOfSession: '',
    category: '',
    level: '',
    frequency: '',
    measurement: '',
    practiceType: '',
    goal: '',
    exercisePriority: '',
    tips: [],
    scoringDescriptions: [],
    exercise: null,
    exerciseUniqueKey: '',
  }

  const fitnessAssessments = ref([])
  const fitnessAssessment = ref(structuredClone(blankFitnessAssessment))

  const isLoading = ref(false)

  const createFitnessAssessment = async fitnessAssessmentItem => {
    isLoading.value = true

    await axios.post(RESOURCE, fitnessAssessmentItem)
      .then(() => {
        showSnackbar('You have successfully created a new fitness assessment', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to create a new fitness assessment:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const deleteFitnessAssessment = async fitnessAssessmentItem => {
    isLoading.value = true
    fitnessAssessmentItem.deleted = true

    await axios.put(RESOURCE, fitnessAssessmentItem)
      .then(() => {
        showSnackbar('You have successfully deleted the fitness assessment', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to delete the fitness assessment:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const fetchFitnessAssessments = async (params = {}) => {
    isLoading.value = true

    await axios.get(RESOURCE, { params: { ...getContentScopeParams(), ...params } })
      .then(response => {
        fitnessAssessments.value = response.data
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to fetch fitness assessments:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const updateFitnessAssessment = async fitnessAssessmentItem => {
    isLoading.value = true

    await axios.put(RESOURCE, fitnessAssessmentItem)
      .then(() => {
        showSnackbar('You have successfully updated the fitness assessment', SnackbarType.SUCCESS)
      })
      .catch(error => {
        const message = error.response?.data?.message ?? error.message
        const statusCode = error.response?.status ?? null

        showSnackbar(
          `Failed to update the fitness assessment:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
          SnackbarType.ERROR,
        )

        throw error
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const updateManyFitnessAssessments = async (type, paramName, paramValue, ids) => {
    isLoading.value = true

    const payload = {
      type,
      params: [
        {
          name: paramName,
          value: paramValue,
          ids,
        },
      ],
    }

    try {
      await axios.put('/library/updateMany', payload)

      showSnackbar(
        `Successfully updated ${ids.length} fitness assessment(s)`,
        SnackbarType.SUCCESS,
      )

      return true
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to update fitness assessments:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetState = () => {
    fitnessAssessments.value = []
    fitnessAssessment.value = structuredClone(blankFitnessAssessment)
    dtSearchQuery.value = ''
    dtExpanded.value = []
    isLoading.value = false
  }

  return {
    dtHeaders,
    dtSearchQuery,
    dtExpanded,

    blankFitnessAssessment,
    fitnessAssessments,
    fitnessAssessment,

    isLoading,

    createFitnessAssessment,
    deleteFitnessAssessment,
    fetchFitnessAssessments,
    updateFitnessAssessment,
    updateManyFitnessAssessments,
    resetState,
  }
}
